import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  CalendarClock,
  ChevronLeft,
  ClipboardList,
  Clock3,
  HeartPulse,
  Loader2,
  MapPin,
  ShieldCheck,
  Smartphone,
  Stethoscope,
  Users,
  type LucideIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TreatmentOverviewHome } from "@/components/consultation/tabs/treatment-overview";
import {
  useDiseaseSearch,
  usePatientOverview,
  usePatientProfileIntake,
} from "@/hooks/usePatientAdmin";
import { cn } from "@/lib/utils";
import type { IntakeCondition } from "@/types";
import { PatientCommunicationLogsPanel } from "./CommunicationLogs";
import { PatientDocuments } from "./PatientDocuments";

type PatientTab = "overview" | "profile-intake" | "patient-records" | "comms";

type PatientProfileProps = {
  patientId?: string;
  tab?: PatientTab;
  embedded?: boolean;
  onBack?: () => void;
};

const getInitials = (value?: string | null) => {
  if (!value) return "P";

  const parts = value
    .split(" ")
    .map((item) => item.trim())
    .filter(Boolean);

  if (parts.length === 0) return "P";

  return parts
    .slice(0, 2)
    .map((item) => item[0]?.toUpperCase() || "")
    .join("");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatDate(value)}, ${formatTime(value)}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatElapsedSince = (value?: string | null) => {
  if (!value) return "—";

  const startDate = new Date(value);
  if (Number.isNaN(startDate.getTime())) return "—";

  const elapsedMs = Math.max(0, Date.now() - startDate.getTime());
  const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));

  if (elapsedDays === 0) return "Today";
  if (elapsedDays < 30) {
    return `${elapsedDays} day${elapsedDays === 1 ? "" : "s"}`;
  }

  const elapsedMonths = Math.floor(elapsedDays / 30);
  if (elapsedMonths < 12) {
    return `${elapsedMonths} month${elapsedMonths === 1 ? "" : "s"}`;
  }

  const elapsedYears = Math.floor(elapsedMonths / 12);
  const remainingMonths = elapsedMonths % 12;

  if (remainingMonths === 0) {
    return `${elapsedYears} year${elapsedYears === 1 ? "" : "s"}`;
  }

  return `${elapsedYears}y ${remainingMonths}m`;
};

const maskPhoneNumber = (value?: string | null) => {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 6) return value;

  const masked = `${digits.slice(0, 2)}${"*".repeat(
    digits.length - 6
  )}${digits.slice(-4)}`;

  return value.trim().startsWith("+") ? `+${masked}` : masked;
};

const maskEmail = (value?: string | null) => {
  if (!value) return "—";
  const [localPart, domain] = value.split("@");
  if (!localPart || !domain) return value;
  if (localPart.length <= 2) return `${localPart}@${domain}`;

  const visibleStart = localPart.slice(0, 2);
  const visibleEnd = localPart.length > 4 ? localPart.slice(-1) : "";
  const hiddenLength = Math.max(
    localPart.length - visibleStart.length - visibleEnd.length,
    2
  );

  return `${visibleStart}${"*".repeat(hiddenLength)}${visibleEnd}@${domain}`;
};

const formatLabelText = (value?: string | null) => {
  if (!value) return "—";

  return value
    .split(/[_-\s]+/)
    .filter(Boolean)
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join(" ");
};

const ValuePill = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <Badge
    variant="outline"
    className={cn(
      "h-6 rounded-full border-border/70 bg-muted/40 px-2.5 z-caption font-medium text-foreground",
      className
    )}
  >
    {children}
  </Badge>
);

const MetadataSeparator = () => (
  <span
    aria-hidden="true"
    className="inline-block h-1 w-1 rounded-full bg-muted-foreground/35"
  />
);

const getStatusBadgeClass = (value?: string | null) => {
  const normalized = value?.toLowerCase() || "";

  if (
    normalized.includes("complete") ||
    normalized.includes("active") ||
    normalized.includes("ready") ||
    normalized.includes("confirmed")
  ) {
    return "border-success/25 bg-success/10 text-success";
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("reserved") ||
    normalized.includes("incomplete") ||
    normalized.includes("draft")
  ) {
    return "border-warning/25 bg-warning/10 text-warning-foreground";
  }

  if (
    normalized.includes("fail") ||
    normalized.includes("cancel") ||
    normalized.includes("invalidat") ||
    normalized.includes("miss")
  ) {
    return "border-error/25 bg-error/10 text-error";
  }

  return "border-border bg-muted text-muted-foreground";
};

const InfoStat = ({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode | null | undefined;
  className?: string;
}) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div
      className={cn(
        "rounded-xl border border-border/70 bg-muted/15 px-3 py-3",
        className
      )}
    >
      <p className="z-label uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <div className="mt-1.5 z-body-md text-foreground">
        {hasValue ? value : "—"}
      </div>
    </div>
  );
};

const SectionSurface = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={cn(
      "overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm",
      className
    )}
  >
    {children}
  </section>
);

const OverviewSectionHeader = ({
  title,
  description,
  icon: Icon,
  action,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
}) => (
  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-primary" /> : null}
        <h2 className="z-h3 text-foreground">{title}</h2>
      </div>
      {description ? (
        <p className="z-secondary text-muted-foreground">{description}</p>
      ) : null}
    </div>
    {action}
  </div>
);

const TeamMemberCard = ({ name, role }: { name: string; role: string }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border/70 bg-card px-3 py-3 shadow-sm">
    <Avatar className="h-12 w-12 border border-border/70">
      <AvatarFallback className="bg-primary/10 z-body-md text-primary">
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <p className="truncate z-body-md text-foreground">{name}</p>
      <p className="mt-1 z-label uppercase tracking-[0.2em] text-muted-foreground">
        {role}
      </p>
    </div>
  </div>
);

const HeroStatCard = ({
  icon: Icon,
  label,
  value,
  description,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number | null | undefined;
  description?: string;
}) => (
  <div className="rounded-xl border border-border/70 bg-card p-3 shadow-sm">
    <div className="flex items-center gap-2.5">
      <div className="rounded-lg border border-border/70 bg-muted/40 p-1.5 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <p className="z-label uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
    </div>
    <p className="mt-3 z-h2 z-numeric text-foreground">
      {value || value === 0 ? value : "—"}
    </p>
    {description ? (
      <p className="mt-2 z-secondary text-muted-foreground">{description}</p>
    ) : null}
  </div>
);

const MetaDetailRow = ({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number | null | undefined;
}) => (
  <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/10 px-3 py-3">
    <div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="z-label uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 break-words z-body text-foreground">
        {value || value === 0 ? value : "—"}
      </p>
    </div>
  </div>
);

const AppointmentTimelineItem = ({
  appointment,
  isLast,
}: {
  appointment: {
    id: string;
    zarity_appointment_id: string;
    status: string;
    type: string;
    start_time: string | null;
    provider_name: string;
    provider_role: string;
    service_name: string;
    is_follow_up: boolean;
    part_of_program: boolean;
    program_name: string | null;
    is_intake_form_filled: boolean;
    has_prescription: boolean;
    has_meal_plan: boolean;
  };
  isLast: boolean;
}) => (
  <article className="relative pl-9">
    <span className="absolute left-3 top-3 z-10 h-2.5 w-2.5 rounded-full bg-primary" />
    {!isLast ? (
      <span className="absolute left-[0.95rem] top-6 h-[calc(100%-0.25rem)] w-px bg-border" />
    ) : null}

    <div className="rounded-xl border border-border/70 bg-card p-4 shadow-sm transition-colors hover:bg-muted/10">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-3">
          <h3 className="z-body-md text-foreground">
            {appointment.service_name || "Appointment"}
          </h3>
          <div className="flex flex-wrap gap-2">
            <ValuePill>{formatDate(appointment.start_time)}</ValuePill>
            <ValuePill>{formatTime(appointment.start_time)}</ValuePill>
            {appointment.is_follow_up ? <ValuePill>Follow-up</ValuePill> : null}
            {appointment.part_of_program ? (
              <ValuePill>{appointment.program_name || "Program"}</ValuePill>
            ) : null}
          </div>
        </div>

        <ValuePill className={getStatusBadgeClass(appointment.status)}>
          {formatLabelText(appointment.status)}
        </ValuePill>
      </div>

      <div className="mt-4 grid gap-3 border-t border-border/60 pt-4 sm:grid-cols-2 xl:grid-cols-4">
        <InfoStat
          label="Appointment ID"
          value={appointment.zarity_appointment_id || "—"}
        />
        <InfoStat
          label="Type"
          value={<ValuePill>{formatLabelText(appointment.type)}</ValuePill>}
        />
        <InfoStat
          label="Provider"
          value={appointment.provider_name || "Unassigned"}
        />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <ValuePill>
          {appointment.is_intake_form_filled
            ? "Intake filled"
            : "Intake pending"}
        </ValuePill>
        <ValuePill>
          {appointment.has_prescription ? "Rx available" : "Rx pending"}
        </ValuePill>
        <ValuePill>
          {appointment.has_meal_plan ? "Meal plan added" : "Meal plan pending"}
        </ValuePill>
      </div>
    </div>
  </article>
);

const ConditionEditorSection = ({
  title,
  value,
  onChange,
  canEdit = false,
}: {
  title: string;
  value: IntakeCondition[];
  onChange: (items: IntakeCondition[]) => void;
  canEdit?: boolean;
}) => {
  const [query, setQuery] = useState("");
  const [customValue, setCustomValue] = useState("");
  const { data: results = [], isLoading } = useDiseaseSearch(query);

  const addDisease = (item: IntakeCondition) => {
    const exists = value.some(
      (entry) =>
        (item.disease_id && entry.disease_id === item.disease_id) ||
        entry.name.toLowerCase() === item.name.toLowerCase()
    );
    if (exists) return;
    onChange([...value, item]);
    setQuery("");
  };

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="z-body-md text-foreground">{title}</h3>
          <p className="z-secondary text-muted-foreground">
            {canEdit
              ? "Search diseases or add a custom condition."
              : "View-only mode. Contact an admin to make changes."}
          </p>
        </div>
        <Badge
          variant="outline"
          className="border-border z-caption text-muted-foreground"
        >
          {value.length} selected
        </Badge>
      </div>

      {canEdit ? (
        <div className="flex flex-col gap-2 lg:flex-row">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search conditions..."
            className="bg-background z-body"
          />
          <Input
            value={customValue}
            onChange={(event) => setCustomValue(event.target.value)}
            placeholder="Add custom condition"
            className="bg-background z-body"
          />
          <Button
            type="button"
            variant="outline"
            className="z-button"
            onClick={() => {
              if (!customValue.trim()) return;
              addDisease({
                id: null,
                disease_id: null,
                name: customValue.trim(),
                is_custom: true,
              });
              setCustomValue("");
            }}
          >
            Add Custom
          </Button>
        </div>
      ) : null}

      {canEdit && query.trim().length >= 2 ? (
        <div className="rounded-lg border border-border bg-background p-3">
          {isLoading ? (
            <div className="flex items-center gap-2 z-secondary text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching conditions...
            </div>
          ) : results.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {results.slice(0, 8).map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="z-button"
                  onClick={() =>
                    addDisease({
                      id: null,
                      disease_id: item.id,
                      name: item.display_name,
                      is_custom: false,
                    })
                  }
                >
                  {item.display_name}
                </Button>
              ))}
            </div>
          ) : (
            <p className="z-secondary text-muted-foreground">
              No matching conditions.
            </p>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {value.length > 0 ? (
          value.map((item, index) => (
            <Badge
              key={`${item.name}-${index}`}
              variant="outline"
              className={cn(
                "border-border bg-muted/30 px-3 py-1 z-body text-foreground",
                canEdit ? "cursor-pointer hover:bg-muted" : ""
              )}
              onClick={() =>
                canEdit
                  ? onChange(
                      value.filter((_, itemIndex) => itemIndex !== index)
                    )
                  : undefined
              }
            >
              {item.name} {canEdit ? "×" : ""}
            </Badge>
          ))
        ) : (
          <p className="z-secondary text-muted-foreground">
            No conditions added yet.
          </p>
        )}
      </div>
    </div>
  );
};

export const PatientProfile = ({
  patientId: patientIdProp,
  tab: tabProp,
  embedded = false,
  onBack,
}: PatientProfileProps) => {
  const navigate = useNavigate();
  const params = useParams<{ patientId: string; tab?: string }>();
  const patientId = patientIdProp || params.patientId;
  const tab = tabProp || params.tab;
  const activeTab: PatientTab =
    tab === "profile-intake"
      ? "profile-intake"
      : tab === "patient-records"
        ? "patient-records"
        : tab === "comms"
          ? "comms"
          : "overview";

  const {
    data: overview,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = usePatientOverview(patientId || "");
  const {
    data: intake,
    isLoading: isIntakeLoading,
    error: intakeError,
  } = usePatientProfileIntake(patientId || "");

  const [healthForm, setHealthForm] = useState({
    height_in_cms: "",
    weight_in_kgs: "",
    city: "",
  });
  const [diseases, setDiseases] = useState<IntakeCondition[]>([]);
  const [fatherHistory, setFatherHistory] = useState<IntakeCondition[]>([]);
  const [motherHistory, setMotherHistory] = useState<IntakeCondition[]>([]);
  const [siblingHistory, setSiblingHistory] = useState<IntakeCondition[]>([]);
  const [alcoholEnabled, setAlcoholEnabled] = useState(false);
  const [alcoholFrequencyId, setAlcoholFrequencyId] = useState<number | null>(
    null
  );
  const [tobaccoEnabled, setTobaccoEnabled] = useState(false);
  const [tobaccoFrequencyId, setTobaccoFrequencyId] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (!intake) return;

    setHealthForm({
      height_in_cms: intake.health.height_in_cms?.toString() || "",
      weight_in_kgs: intake.health.weight_in_kgs?.toString() || "",
      city: intake.health.city || "",
    });
    setDiseases(intake.diseases);
    setFatherHistory(intake.family_history.father);
    setMotherHistory(intake.family_history.mother);
    setSiblingHistory(intake.family_history.sibling);
    setAlcoholEnabled(intake.lifestyle.alcohol.enabled);
    setAlcoholFrequencyId(intake.lifestyle.alcohol.frequency_id);
    setTobaccoEnabled(intake.lifestyle.tobacco.enabled);
    setTobaccoFrequencyId(intake.lifestyle.tobacco.frequency_id);
  }, [intake]);

  if (!patientId) {
    return null;
  }

  const patient = overview?.patient;
  const completionPercentage =
    overview?.status.completion_percentage ??
    intake?.status.completion_percentage ??
    0;

  const renderOverview = () => {
    if (isOverviewLoading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index} className="border-border bg-card">
              <CardHeader className="p-4 pb-3">
                <Skeleton className="h-5 w-40" />
              </CardHeader>
              <CardContent className="space-y-3 p-4 pt-0">
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (overviewError || !overview) {
      return (
        <Card className="border-border bg-card">
          <CardContent className="flex min-h-[240px] flex-col items-center justify-center gap-3">
            <p className="text-sm text-muted-foreground">
              Unable to load patient overview.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        <SectionSurface>
          <OverviewSectionHeader title="Journey" icon={CalendarClock} />
          <Separator />
          <div className="p-4">
            {overview.recent_appointments.length > 0 ? (
              <div className="space-y-4">
                {overview.recent_appointments.map((appointment, index) => (
                  <AppointmentTimelineItem
                    key={appointment.id}
                    appointment={appointment}
                    isLast={index === overview.recent_appointments.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border/80 bg-muted/10 px-4 py-6 text-sm text-muted-foreground">
                No recent appointments found.
              </div>
            )}
          </div>
        </SectionSurface>

        <SectionSurface>
          <OverviewSectionHeader
            title="Device & Access"
            description="App engagement, device state, and access details."
            icon={Smartphone}
          />
          <Separator />
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <MetaDetailRow
              icon={Smartphone}
              label="Known Devices"
              value={overview.devices.total}
            />
            <MetaDetailRow
              icon={ShieldCheck}
              label="Last App Activity"
              value={formatDateTime(overview.status.last_app_activity_at)}
            />
            <MetaDetailRow
              icon={Clock3}
              label="Latest Device"
              value={
                overview.devices.latest
                  ? `${overview.devices.latest.platform || "—"} · ${overview.devices.latest.model || "—"}`
                  : "—"
              }
            />
            <MetaDetailRow
              icon={ClipboardList}
              label="App Version"
              value={overview.devices.latest?.app_version || "—"}
            />
          </div>
        </SectionSurface>
      </div>
    );
  };

  const renderProfileIntake = () => {
    if (isIntakeLoading) {
      return (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)]">
          <Card className="border-border bg-card">
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-48 w-full" />
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
      );
    }

    if (intakeError || !intake) {
      return (
        <Card className="border-border bg-card">
          <CardContent className="flex min-h-[240px] flex-col items-center justify-center gap-3">
            <p className="text-sm text-muted-foreground">
              Unable to load profile intake data.
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)]">
        <div className="space-y-4">
          <Card className="border-border/60 bg-card">
            <CardHeader className="gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="text-lg text-foreground">
                    Submitted Profile Intake
                  </CardTitle>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Read-only view of the patient-submitted profile intake data.
                  </p>
                </div>
                <Badge
                  variant={intake.status.is_complete ? "default" : "outline"}
                >
                  {intake.status.is_complete ? "Complete" : "Incomplete"}
                </Badge>
              </div>

              <div className="rounded-xl border border-border/70 bg-muted/15 p-4">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Completion
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-foreground">
                      {intake.status.completion_percentage}%
                    </p>
                  </div>
                  <ValuePill>{intake.status.status}</ValuePill>
                </div>
                <Progress
                  value={intake.status.completion_percentage}
                  className="mt-4 h-2.5"
                />
                <p className="mt-3 text-sm text-muted-foreground">
                  Updated {formatDateTime(intake.status.updated_at)}
                </p>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <Accordion
                type="multiple"
                defaultValue={[
                  "body-metrics",
                  "conditions",
                  "family-history",
                  "lifestyle",
                ]}
                className="w-full"
              >
                <AccordionItem value="body-metrics" className="border-border">
                  <AccordionTrigger className="py-5 text-left text-sm font-semibold text-foreground hover:no-underline">
                    Vitals & Body Metrics
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <InfoStat
                        label="Height (cm)"
                        value={healthForm.height_in_cms}
                      />
                      <InfoStat
                        label="Weight (kg)"
                        value={healthForm.weight_in_kgs}
                      />
                      <InfoStat label="BMI" value={intake.health.bmi} />
                      <InfoStat label="City" value={healthForm.city} />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="conditions" className="border-border">
                  <AccordionTrigger className="py-5 text-left text-sm font-semibold text-foreground hover:no-underline">
                    Diagnosed Conditions
                  </AccordionTrigger>
                  <AccordionContent>
                    <ConditionEditorSection
                      title="Diagnosed Conditions"
                      value={diseases}
                      onChange={setDiseases}
                      canEdit={false}
                    />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="family-history" className="border-border">
                  <AccordionTrigger className="py-5 text-left text-sm font-semibold text-foreground hover:no-underline">
                    Family History
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4">
                      <ConditionEditorSection
                        title="Father"
                        value={fatherHistory}
                        onChange={setFatherHistory}
                        canEdit={false}
                      />
                      <ConditionEditorSection
                        title="Mother"
                        value={motherHistory}
                        onChange={setMotherHistory}
                        canEdit={false}
                      />
                      <ConditionEditorSection
                        title="Sibling"
                        value={siblingHistory}
                        onChange={setSiblingHistory}
                        canEdit={false}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="lifestyle" className="border-border">
                  <AccordionTrigger className="py-5 text-left text-sm font-semibold text-foreground hover:no-underline">
                    Lifestyle
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <Card className="border-border bg-card">
                        <CardHeader>
                          <CardTitle className="text-base text-foreground">
                            Alcohol Use
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm text-muted-foreground">
                          <p>
                            {alcoholEnabled
                              ? "Currently enabled"
                              : "No alcohol use recorded"}
                          </p>
                          {alcoholEnabled && alcoholFrequencyId ? (
                            <p>
                              Frequency:{" "}
                              {intake.lifestyle.frequency_options.find(
                                (option) => option.id === alcoholFrequencyId
                              )?.label || "—"}
                            </p>
                          ) : null}
                        </CardContent>
                      </Card>

                      <Card className="border-border bg-card">
                        <CardHeader>
                          <CardTitle className="text-base text-foreground">
                            Tobacco Use
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm text-muted-foreground">
                          <p>
                            {tobaccoEnabled
                              ? "Currently enabled"
                              : "No tobacco use recorded"}
                          </p>
                          {tobaccoEnabled && tobaccoFrequencyId ? (
                            <p>
                              Frequency:{" "}
                              {intake.lifestyle.frequency_options.find(
                                (option) => option.id === tobaccoFrequencyId
                              )?.label || "—"}
                            </p>
                          ) : null}
                        </CardContent>
                      </Card>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <SectionSurface>
            <OverviewSectionHeader
              title="Submission Snapshot"
              description="Fast summary for intake completion and current stats."
              icon={ClipboardList}
            />
            <Separator />
            <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-1">
              <InfoStat
                label="Completion %"
                value={intake.status.completion_percentage}
              />
              <InfoStat label="Status" value={intake.status.status} />
              <InfoStat
                label="Updated At"
                value={formatDateTime(intake.status.updated_at)}
              />
              <InfoStat label="BMI" value={intake.health.bmi} />
            </div>
          </SectionSurface>

          <SectionSurface>
            <OverviewSectionHeader
              title="Lifestyle Summary"
              description="Current habits captured in the intake flow."
              icon={ShieldCheck}
            />
            <Separator />
            <div className="space-y-3 p-4">
              <MetaDetailRow
                icon={Stethoscope}
                label="Alcohol Use"
                value={
                  alcoholEnabled
                    ? intake.lifestyle.frequency_options.find(
                        (option) => option.id === alcoholFrequencyId
                      )?.label || "Enabled"
                    : "No"
                }
              />
              <MetaDetailRow
                icon={Stethoscope}
                label="Tobacco Use"
                value={
                  tobaccoEnabled
                    ? intake.lifestyle.frequency_options.find(
                        (option) => option.id === tobaccoFrequencyId
                      )?.label || "Enabled"
                    : "No"
                }
              />
              <MetaDetailRow
                icon={MapPin}
                label="City"
                value={healthForm.city}
              />
              <MetaDetailRow
                icon={HeartPulse}
                label="Diagnosed Conditions"
                value={diseases.length}
              />
            </div>
          </SectionSurface>
        </div>
      </div>
    );
  };

  const renderPatientRecords = () => (
    <div className="flex flex-col gap-4">
      <Card className="border-border/60 bg-card">
        <CardContent className="min-h-[420px] p-4">
          <TreatmentOverviewHome
            patientId={patientId}
            showHeader={false}
            embedded
          />
        </CardContent>
      </Card>
      <PatientDocuments
        patientId={patientId}
        appointmentId={overview?.recent_appointments?.[0]?.id}
      />
    </div>
  );

  const renderComms = () => (
    <PatientCommunicationLogsPanel
      lockedPatient={{
        id: patientId,
        first_name: overview?.patient.first_name,
        last_name: overview?.patient.last_name,
        phone_number: overview?.patient.phone_number,
        zarity_user_id: overview?.patient.zarity_user_id,
      }}
    />
  );

  const patientRouteBase = patientId ? `/patients/${patientId}` : "/patients";

  return (
    <div className="h-full min-h-full bg-background text-foreground overflow-y-auto overflow-x-hidden">
      <div
        className={
          embedded ? "flex flex-col gap-4" : "flex flex-col gap-4 p-3 sm:p-4"
        }
      >
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            navigate(`${patientRouteBase}/${value as PatientTab}`)
          }
          className="flex flex-col gap-4"
        >
          <Card className="overflow-hidden border-border/60 bg-card">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.9fr)]">
                <div className="relative overflow-hidden p-4 sm:p-5">
                  <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-r from-primary/10 via-muted/60 to-transparent" />

                  <div className="relative flex flex-col gap-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex items-start gap-3">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 shrink-0 rounded-md bg-background"
                          onClick={() => {
                            if (onBack) {
                              onBack();
                            } else {
                              navigate("/patients");
                            }
                          }}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>

                        <Avatar className="h-14 w-14 border border-border/70 shadow-sm">
                          <AvatarFallback className="bg-primary/10 text-base font-semibold text-primary">
                            {getInitials(patient?.name)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h1 className="z-h1 text-foreground">
                              {patient?.name ||
                                (isOverviewLoading
                                  ? "Loading patient..."
                                  : "Patient profile")}
                            </h1>
                            {patient?.is_active !== undefined && (
                              <ValuePill
                                className={
                                  patient?.is_active
                                    ? "border-primary/30 bg-primary/10 text-primary"
                                    : "border-border bg-muted text-muted-foreground"
                                }
                              >
                                {patient?.is_active ? "Active" : "Inactive"}
                              </ValuePill>
                            )}
                          </div>

                          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 z-secondary text-muted-foreground">
                            <span className="z-numeric">{`${patient?.age ?? "—"} / ${patient?.gender || "—"}`}</span>
                            <MetadataSeparator />
                            <span>{patient?.city || "—"}</span>
                            <MetadataSeparator />
                            <span>{patient?.timezone || "—"}</span>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-2">
                            <ValuePill className="z-numeric">
                              {maskPhoneNumber(patient?.phone_number)}
                            </ValuePill>
                            <ValuePill>{maskEmail(patient?.email)}</ValuePill>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                        {overview?.clinical_snapshot.active_program ? (
                          <ValuePill>
                            {overview.clinical_snapshot.active_program}
                          </ValuePill>
                        ) : null}
                        <ValuePill className="font-mono">
                          Patient ID{" "}
                          {patient?.zarity_user_id || patientId || "—"}
                        </ValuePill>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      <HeroStatCard
                        icon={CalendarClock}
                        label="Appointments Made"
                        value={overview?.journey.total_appointments}
                        description="Total appointments recorded for this patient."
                      />
                      <HeroStatCard
                        icon={Activity}
                        label="Journey Length"
                        value={formatElapsedSince(patient?.created_at)}
                        description="Time elapsed since the patient downloaded the app."
                      />
                      <HeroStatCard
                        icon={ClipboardList}
                        label="Profile Completion"
                        value={`${completionPercentage}%`}
                        description="Completion across submitted patient intake information."
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-border/60 bg-muted/20 p-4 xl:border-l xl:border-t-0">
                  <div className="flex h-full flex-col gap-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-foreground">
                          Care Team
                        </p>
                      </div>
                      <div className="rounded-lg border border-border/70 bg-background p-2 text-primary">
                        <Users className="h-4 w-4" />
                      </div>
                    </div>

                    <div className="grid gap-2.5">
                      <TeamMemberCard
                        name={
                          overview?.care_team.care_manager?.name || "Unassigned"
                        }
                        role="Care Manager"
                      />
                      <TeamMemberCard
                        name={
                          overview?.care_team.nutritionist?.name || "Unassigned"
                        }
                        role="Nutritionist"
                      />
                      <TeamMemberCard
                        name={
                          overview?.care_team.physiotherapist?.name ||
                          "Unassigned"
                        }
                        role="Physiotherapist"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="overflow-x-auto">
            <TabsList className="inline-flex h-auto min-w-max gap-1 rounded-xl border border-border bg-muted p-1 text-muted-foreground">
              <TabsTrigger
                value="overview"
                className="rounded-lg px-3 py-1.5 z-button data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Journey
              </TabsTrigger>
              <TabsTrigger
                value="profile-intake"
                className="rounded-lg px-3 py-1.5 z-button data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Profile Info
              </TabsTrigger>
              <TabsTrigger
                value="patient-records"
                className="rounded-lg px-3 py-1.5 z-button data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Medical Records
              </TabsTrigger>
              <TabsTrigger
                value="comms"
                className="rounded-lg px-3 py-1.5 z-button data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                Comms
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex flex-col rounded-xl border border-border bg-card p-3 sm:p-4">
            <div>
              <TabsContent value="overview" className="mt-0">
                {renderOverview()}
              </TabsContent>
              <TabsContent value="profile-intake" className="mt-0">
                {renderProfileIntake()}
              </TabsContent>
              <TabsContent value="patient-records" className="mt-0">
                {renderPatientRecords()}
              </TabsContent>
              <TabsContent value="comms" className="mt-0">
                {renderComms()}
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientProfile;
